/**
 * client/src/components/premium/dot-grid.tsx
 *
 * Subtle "cryptographic dot" texture, used as an absolute-positioned overlay
 * inside premium gradient panels. Extracted from the setup-flow WelcomeStep so
 * the logged-in app shares one texture source rather than each surface
 * re-inlining the SVG (Reuse-First — DEVELOPMENT/code-style.md).
 *
 * Defaults to a NEUTRAL slate tone — the texture is chrome, and emerald is
 * reserved for genuine milestones (CLAUDE.md Design Constraints). A milestone
 * context may opt into the emerald tint by passing `color`.
 *
 * Purely decorative: aria-hidden, pointer-events-none, and low opacity so it
 * never competes with content contrast (WCAG — DESIGN-SYSTEM.md §6.1).
 */

import { useId } from "react";

interface DotGridProps {
  /** Dot colour. Defaults to a neutral slate — NOT the milestone emerald. */
  color?: string;
  /** Overlay opacity (0–1). Kept low so body text keeps its contrast. */
  opacity?: number;
  /** Grid pitch in px. */
  gap?: number;
}

export function DotGrid({ color = "#64748B", opacity = 0.08, gap = 20 }: DotGridProps) {
  // A per-instance id (useId) keeps multiple DotGrids on one page from
  // colliding on the shared SVG <defs> id — colons stripped so the url(#…)
  // reference stays valid CSS.
  const patternId = `dot-grid-${useId().replace(/:/g, "")}`;
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ opacity }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id={patternId} x="0" y="0" width={gap} height={gap} patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.5" fill={color} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
}
